import { useMemo, useState } from "react";
import Input from "components/common/Input";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import FormHeader from "components/common/FormHeader";
import { Col, Row } from "antd";
import CustomSelector from "components/common/Selector";
import Colors from "assets/Colors";
import { useFormik } from "formik";
import { BSON } from "realm-web";
import usePriceListMutations from "graphql/usePriceListMutations";
import { useMessaging } from "context/MessagingContext";
import s from "./styles";

function AddList({
  closeModal,
  editItem = {},
  priceListsOptions,
  priceLists,
  onAddSuccess,
  onUpdateSuccess,
  items,
}) {
  const { t } = useTranslation();

  const [type, setType] = useState("new");
  const [dbLoading, setDbLoading] = useState(false);
  const [listToClone, setListToClone] = useState(null);

  const [typeIncrement, setTypeIncrement] = useState(null);

  const [incrementValue, setIncrementValue] = useState(null);

  const [typeRound, setTypeRound] = useState(null);

  const [roundValue, setRoundValue] = useState(null);

  const { setErrorMessage } = useMessaging();

  const { addPriceList, updatePriceList } = usePriceListMutations();

  const listToCopy = useMemo(() => {
    let priceList = [];

    if (listToClone === "default") {
      priceList = items.map((item) => ({
        itemId: item._id,
        price: item.price,
      }));
    } else {
      priceList = priceLists.find((list) => list._id === listToClone)?.items;
    }

    return priceList || [];
  }, [items, listToClone, priceLists]);

  const {
    values,
    handleSubmit,
    setFieldValue,
    getFieldProps,
    isValid,
    dirty,
    touched,
    errors,
  } = useFormik({
    initialValues: {
      _id: editItem?._id || new BSON.ObjectId(),
      name: editItem?.name || "",
      items: editItem?.items || [],
      updateDatetime: new Date(),
      creationDatetime: editItem?.creationDatetime || new Date(),
    },

    onSubmit: (values) => {
      if (!editItem) {
        setDbLoading(true);
        addPriceList(values)
          .then(() => {
            onAddSuccess();
            setDbLoading(false);
            closeModal();
          })
          .catch((e) => setErrorMessage({ error: e }));
      } else {
        setDbLoading(true);
        updatePriceList(editItem, values)
          .then(() => {
            onUpdateSuccess();
            setDbLoading(false);
            closeModal();
          })
          .catch((err) => {
            setDbLoading(false);
            setErrorMessage({ error: err });
          });
      }
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Required"),
    }),
  });

  useMemo(() => {
    if (listToCopy) {
      const sd = listToCopy.map(({ itemId, price }) => {
        if (incrementValue) {
          switch (typeIncrement) {
            case "+":
              price = parseFloat(price) + parseFloat(incrementValue);
              break;
            case "-":
              price = parseFloat(price) - parseFloat(incrementValue);
              break;
            case "+%":
              price =
                parseFloat(price) +
                (parseFloat(price) * parseFloat(incrementValue)) / 100;
              break;
            case "-%":
              price =
                parseFloat(price) -
                (parseFloat(price) * parseFloat(incrementValue)) / 100;
              break;
            default:
              break;
          }
        }
        if (typeRound) {
          switch (typeRound) {
            case "+":
              if (price % 1 > roundValue) {
                price = Math.ceil(price);
              }
              break;
            case "-":
              if (price % 1 > roundValue) {
                price = Math.floor(price);
              }
              break;
            default:
              break;
          }
        }
        if (roundValue) {
          price = parseFloat(price) + parseFloat(roundValue);
        }
        return {
          itemId,
          price: `${price}`,
          _id: new BSON.ObjectId(),
        };
      });
      setFieldValue("items", sd);
    }
    if (!incrementValue && editItem?.items) {
      setFieldValue("items", editItem.items);
    }
  }, [
    editItem,
    incrementValue,
    listToCopy,
    roundValue,
    setFieldValue,
    typeIncrement,
    typeRound,
  ]);

  console.log("values", values.items[0]);

  const disabledEdit = editItem && editItem._id == "default";

  return (
    <>
      <FormHeader
        addTitle={t("containers.InventoryPriceList.addList.newList")}
        updateTitle={t("containers.InventoryPriceList.addList.editList")}
        closeModal={closeModal}
        updating={editItem && Object.keys(editItem).length > 0}
        saving={dbLoading}
        onSave={handleSubmit}
        enabled={isValid && dirty}
      />

      <s.BodyContainer>
        <s.Title>{t("containers.InventoryPriceList.addList.infoList")}</s.Title>
        <s.Line style={{ marginTop: "18px", marginBottom: "40px" }} />

        <Row gutter={[10, 10]}>
          <Col xs={24} sm={12} md={6}>
            <s.SubTitle>{t("Items.fields.type.label")}</s.SubTitle>
            <CustomSelector
              disabled={disabledEdit}
              borderColor={Colors.text_secondary}
              options={[
                {
                  name: t("containers.InventoryPriceList.addList.newList"),
                  value: "new",
                },
                {
                  name: t("containers.InventoryPriceList.addList.doubleList"),
                  value: "double",
                },
              ]}
              style={{ width: "100%" }}
              value={type}
              placeholder={t(
                "containers.InventoryPriceList.addList.selectList"
              )}
              onChange={(value) => setType(value)}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <s.SubTitle>{t("Items.fields.name.label")}*</s.SubTitle>
            <Input
              disabled={disabledEdit}
              placeholder={t("Items.fields.name.placeholder")}
              valid={touched.name && errors.name}
              style={{
                borderRadius: "4px",
              }}
              {...getFieldProps("name")}
            />
            {touched.name && errors.name && (
              <s.messageAlert>{t("formValidations.required")}</s.messageAlert>
            )}
          </Col>
        </Row>

        {type === "double" && (
          <>
            <s.SectionTitle>
              <s.Title style={{ marginTop: "40px" }}>
                {t(
                  "containers.InventoryPriceList.addList.advancedEditingOfNewList"
                )}
              </s.Title>
            </s.SectionTitle>
            <s.Line style={{ marginTop: "18px", marginBottom: "10px" }} />
            <Row gutter={[10, 10]}>
              <Col xs={24} sm={12} md={6}>
                <s.SubTitle>
                  {t("containers.InventoryPriceList.addList.listToCopy")}
                </s.SubTitle>
                <CustomSelector
                  borderColor={Colors.text_secondary}
                  options={priceListsOptions
                    .filter((option) => option.value !== null)
                    .map((list) => {
                      return {
                        name: list.name,
                        value: list.value,
                      };
                    })}
                  style={{ width: "100%" }}
                  placeholder={t(
                    "containers.InventoryPriceList.addList.selectList"
                  )}
                  value={listToClone}
                  onChange={(value) => setListToClone(value)}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <s.SubTitle>
                  {t("containers.InventoryPriceList.addList.itemsToCopy")}
                </s.SubTitle>
                <CustomSelector
                  borderColor={Colors.text_secondary}
                  options={[
                    {
                      name: t(
                        "containers.InventoryPriceList.addList.onlyCopyExistingItems"
                      ),
                      value: "common",
                    },
                  ]}
                  style={{ width: "100%" }}
                  placeholder={t(
                    "containers.InventoryPriceList.addList.selectType"
                  )}
                />
              </Col>
            </Row>

            <s.SectionTitle>
              <s.Title style={{ marginTop: "40px" }}>
                {t("containers.InventoryPriceList.addList.copyOptions")}
              </s.Title>
            </s.SectionTitle>
            <s.Line style={{ marginTop: "18px", marginBottom: "10px" }} />
            <Row gutter={[10, 10]}>
              <Col xs={24} sm={12} md={6}>
                <s.SubTitle>
                  {t("containers.InventoryPriceList.addList.incremetType")}
                </s.SubTitle>
                <CustomSelector
                  disabled={!listToClone}
                  borderColor={Colors.text_secondary}
                  options={[
                    {
                      name: t("containers.InventoryPriceList.addList.none"),
                      value: "none",
                    },
                    {
                      name: t(
                        "containers.InventoryPriceList.addList.percentagePlus"
                      ),
                      value: "+%",
                    },
                    {
                      name: t(
                        "containers.InventoryPriceList.addList.fixedPlus"
                      ),
                      value: "+",
                    },
                    {
                      name: t(
                        "containers.InventoryPriceList.addList.percentageMinus"
                      ),
                      value: "-%",
                    },
                    {
                      name: t(
                        "containers.InventoryPriceList.addList.fixedMinus"
                      ),
                      value: "-",
                    },
                  ]}
                  style={{ width: "100%" }}
                  placeholder={t(
                    "containers.InventoryPriceList.addList.selectType"
                  )}
                  onChange={(value) => setTypeIncrement(value)}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <s.SubTitle>
                  {t("containers.InventoryPriceList.addList.incrementValue")}
                </s.SubTitle>
                <Input
                  disabled={!typeIncrement || typeIncrement === "none"}
                  placeholder={0}
                  type="number"
                  style={{
                    // marginBottom: "27px",
                    borderRadius: "4px",
                  }}
                  name="incrementValue"
                  onChange={(e) => setIncrementValue(e.target.value)}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <s.SubTitle>
                  {t("containers.InventoryPriceList.addList.additionalAction")}
                </s.SubTitle>
                <CustomSelector
                  disabled={
                    !typeIncrement ||
                    incrementValue == null ||
                    typeIncrement === "none" ||
                    typeIncrement === "+" ||
                    typeIncrement === "-"
                  }
                  borderColor={Colors.text_secondary}
                  options={[
                    {
                      name: t("containers.InventoryPriceList.addList.none"),
                      value: "none",
                    },
                    {
                      name: t("containers.InventoryPriceList.addList.roundUp"),
                      value: "+",
                    },
                    {
                      name: t(
                        "containers.InventoryPriceList.addList.roundDown"
                      ),
                      value: "-",
                    },
                  ]}
                  style={{ width: "100%" }}
                  placeholder={t(
                    "containers.InventoryPriceList.addList.selectType"
                  )}
                  onChange={(value) => setTypeRound(value)}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <s.SubTitle>
                  {t("containers.InventoryPriceList.addList.additionalAction")}
                </s.SubTitle>
                <CustomSelector
                  disabled={!typeRound || typeRound === "none"}
                  borderColor={Colors.text_secondary}
                  options={[
                    {
                      name: "0.05",
                      value: "0.05",
                    },
                    {
                      name: "0.10",
                      value: "0.10",
                    },
                    {
                      name: "0.50",
                      value: "0.50",
                    },
                    {
                      name: "1.00",
                      value: "0.00",
                    },
                  ]}
                  style={{ width: "100%" }}
                  placeholder={t(
                    "containers.InventoryPriceList.addList.selectType"
                  )}
                  onChange={(value) => setRoundValue(value)}
                />
              </Col>
            </Row>
          </>
        )}
      </s.BodyContainer>
    </>
  );
}

export default AddList;
